/**
 * Benchmark entries for the Evals config editor: the row shape the editor
 * holds (every value a string, so inputs stay controlled), the conversions
 * to and from the api's BenchmarkEntry, and the rules a save has to pass.
 *
 * An entry names its source (a GitHub repository at a ref, or an S3 prefix
 * read through one of the team's connections), the TaskHopper adapter that
 * runs it with the inputs its descriptor declares, and the eval group it
 * fills. The rules mirror the api's BenchmarkEntry, which every cron
 * validates when it reads a row. The server refuses a bad entry either
 * way; validating here means the operator reads the reason on the field
 * instead of in a toast.
 */

import type {
  BenchmarkAdapterItem,
  BenchmarkEntry,
  GithubSourceConfig,
  S3SourceConfig,
} from '@/api/generated/model'
import { SLUG_REGEX } from '@/lib/slug'

/** One row of a name-to-value map (an option, a route, an alias). */
export interface PairRow {
  key: string
  value: string
  /**
   * Options only: the value as it was stored, JSON type and all. The input
   * shows it as text, and text alone cannot say whether `4` was a number or
   * a string, so `optionValue` sends this back while the text is unchanged.
   */
  loaded?: string | number | boolean
}

export interface GithubSourceRow {
  kind: 'github'
  repo: string
  ref: string
}

export interface S3SourceRow {
  kind: 's3'
  connection_id: string
  prefix: string
  /** One ignored prefix per line. */
  ignore: string
}

export type SourceRow = GithubSourceRow | S3SourceRow
export type SourceKind = SourceRow['kind']

export interface HarnessRow {
  adapter: string
  options: PairRow[]
  routes: PairRow[]
  aliases: PairRow[]
  /** Empty for the night's default. */
  concurrency: string
}

export interface GroupRow {
  slug: string
  display: string
}

/** An entry as the editor holds it. */
export interface EntryRow {
  source: SourceRow
  harness: HarnessRow
  group: GroupRow
}

export const SOURCE_KINDS: readonly SourceKind[] = ['github', 's3']

export const SOURCE_KIND_LABELS: Record<SourceKind, string> = {
  github: 'GitHub repository',
  s3: 'S3 prefix',
}

const OPTION_NAME_REGEX = /^[A-Za-z][A-Za-z0-9_]*$/
const NATIVE_MODEL_REGEX = /^\S{1,255}$/
const CATALOG_MODEL_REGEX = /^[a-z0-9_-]+\/\S+$/
/** s3://bucket/key with a non-empty key. The trailing slash is checked apart. */
const S3_PREFIX_REGEX = /^s3:\/\/[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]\/.+$/
const MAX_OPTIONS = 32
const MAX_CONCURRENCY = 32

export function newSource(kind: SourceKind): SourceRow {
  return kind === 'github'
    ? { kind: 'github', repo: '', ref: '' }
    : { kind: 's3', connection_id: '', prefix: '', ignore: '' }
}

export function newEntry(): EntryRow {
  return {
    source: newSource('github'),
    harness: { adapter: '', options: [], routes: [], aliases: [], concurrency: '' },
    group: { slug: '', display: '' },
  }
}

function pairs(map: Record<string, string> | undefined): PairRow[] {
  return Object.entries(map ?? {}).map(([key, value]) => ({ key, value }))
}

/** Option rows keep the stored value beside its text (`PairRow.loaded`). */
function optionPairs(map: Record<string, string | number | boolean> | undefined): PairRow[] {
  return Object.entries(map ?? {}).map(([key, value]) => ({
    key,
    value: String(value),
    loaded: value,
  }))
}

function sourceFromConfig(source: GithubSourceConfig | S3SourceConfig): SourceRow {
  if (source.kind === 'github') return { kind: 'github', repo: source.repo, ref: source.ref }
  return {
    kind: 's3',
    connection_id: source.connection_id,
    prefix: source.prefix,
    ignore: (source.ignore ?? []).join('\n'),
  }
}

export function entryFromConfig(entry: BenchmarkEntry): EntryRow {
  const concurrency = entry.harness.concurrency
  return {
    source: sourceFromConfig(entry.source),
    harness: {
      adapter: entry.harness.adapter,
      options: optionPairs(entry.harness.options),
      routes: pairs(entry.harness.routes),
      aliases: pairs(entry.harness.aliases),
      concurrency: concurrency === null || concurrency === undefined ? '' : String(concurrency),
    },
    group: { slug: entry.group.slug, display: entry.group.display },
  }
}

/** Rows as a save sends them: trimmed, with rows left entirely blank dropped. */
export function trimPairs(rows: readonly PairRow[]): PairRow[] {
  return rows
    .map((row) => ({ ...row, key: row.key.trim(), value: row.value.trim() }))
    .filter((row) => row.key !== '' || row.value !== '')
}

/**
 * What an option row sends to the adapter. A stored value whose text is
 * unchanged goes back exactly as it was stored: a string option that reads
 * as a number stays that exact string, however long. Text the operator
 * typed is read as they would read it: true and false are booleans, a
 * whole number is a number, anything else is text.
 */
export function optionValue(row: PairRow): string | number | boolean {
  const text = row.value.trim()
  if (row.loaded !== undefined && text === String(row.loaded)) return row.loaded
  if (text === 'true') return true
  if (text === 'false') return false
  if (/^-?\d+$/.test(text)) return Number(text)
  return text
}

/** A one-per-line text box as a list: each line trimmed, blanks dropped. */
export function nonBlankLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
}

function sourceToBody(source: SourceRow): GithubSourceConfig | S3SourceConfig {
  if (source.kind === 'github') {
    return { kind: 'github', repo: source.repo.trim(), ref: source.ref.trim() }
  }
  return {
    kind: 's3',
    connection_id: source.connection_id.trim(),
    prefix: source.prefix.trim(),
    ignore: nonBlankLines(source.ignore),
  }
}

function mapOf(rows: readonly PairRow[]): Record<string, string> {
  return Object.fromEntries(trimPairs(rows).map((row) => [row.key, row.value]))
}

export function entryToBody(row: EntryRow): BenchmarkEntry {
  const concurrency = row.harness.concurrency.trim()
  return {
    source: sourceToBody(row.source),
    harness: {
      adapter: row.harness.adapter.trim(),
      options: Object.fromEntries(
        trimPairs(row.harness.options).map((pair) => [pair.key, optionValue(pair)]),
      ),
      routes: mapOf(row.harness.routes),
      aliases: mapOf(row.harness.aliases),
      concurrency: concurrency === '' ? null : Number(concurrency),
    },
    group: { slug: row.group.slug.trim(), display: row.group.display.trim() },
  }
}

/**
 * One canonical string per list, so the dirty check ignores what a save
 * would drop anyway.
 */
export function entriesKey(rows: readonly EntryRow[]): string {
  return JSON.stringify(rows.map(entryToBody))
}

/**
 * Whether a path stays inside the tree it is relative to, mirroring the
 * api's `repo_relative_path`. The jobs resolve these against a checkout or
 * a fetched prefix, so an absolute or climbing value points outside it.
 */
export function escapesCheckout(path: string): boolean {
  return (
    path.startsWith('/') ||
    path === '..' ||
    path.startsWith('../') ||
    path.includes('/../') ||
    path.endsWith('/..')
  )
}

function validateSource(n: string, source: SourceRow): string | null {
  if (source.kind === 'github') {
    if (source.repo.trim() === '') return `Benchmark ${n} needs a repository.`
    if (source.ref.trim() === '') return `Benchmark ${n} needs a branch or tag.`
    return null
  }
  if (source.connection_id.trim() === '') {
    return `Benchmark ${n} needs one of the team's S3 connections.`
  }
  const prefix = source.prefix.trim()
  if (!S3_PREFIX_REGEX.test(prefix) || prefix.endsWith('/')) {
    return `Benchmark ${n} needs an S3 prefix like s3://bucket/path, with no trailing slash.`
  }
  if (nonBlankLines(source.ignore).some(escapesCheckout)) {
    return `Benchmark ${n} has an ignore prefix that leaves the S3 prefix. Use paths relative to it, with no leading slash and no "..".`
  }
  return null
}

function duplicateKey(rows: readonly PairRow[]): string | undefined {
  const keys = trimPairs(rows).map((row) => row.key)
  return keys.find((key, index) => keys.indexOf(key) !== index)
}

function validateHarness(n: string, harness: HarnessRow): string | null {
  if (harness.adapter.trim() === '') return `Benchmark ${n} needs a TaskHopper adapter.`
  const options = trimPairs(harness.options)
  if (options.length > MAX_OPTIONS) return `Benchmark ${n} lists more than ${String(MAX_OPTIONS)} options.`
  if (options.some((row) => !OPTION_NAME_REGEX.test(row.key))) {
    return `Benchmark ${n} has an option name that is not one an adapter declares. Start with a letter, then letters, digits and underscores.`
  }
  const option = duplicateKey(harness.options)
  if (option !== undefined) return `Benchmark ${n} lists the option ${option} twice.`
  const aliases = trimPairs(harness.aliases)
  if (aliases.some((row) => !NATIVE_MODEL_REGEX.test(row.key))) {
    return `Benchmark ${n} needs a model name with no spaces for each alias.`
  }
  const alias = aliases.find((row) => !CATALOG_MODEL_REGEX.test(row.value))
  if (alias !== undefined) {
    return `Benchmark ${n} needs a catalog model like provider/model for the alias ${alias.key}.`
  }
  const again = duplicateKey(harness.aliases)
  if (again !== undefined) return `Benchmark ${n} lists the alias ${again} twice.`
  const concurrency = harness.concurrency.trim()
  if (concurrency !== '' && !(/^\d+$/.test(concurrency) && Number(concurrency) >= 1 && Number(concurrency) <= MAX_CONCURRENCY)) {
    return `Benchmark ${n} needs a concurrency from 1 to ${String(MAX_CONCURRENCY)}, or none.`
  }
  return null
}

/**
 * What the night's route check (`adapter_routes.route_models`) would
 * refuse, against the picked adapter's declared routes: a model for a
 * route the adapter does not open or picks itself, and a caller route
 * with no catalog model. A stored adapter the platform no longer ships
 * has no declaration here, and the api keeps its stored routes.
 */
function validateRoutes(
  n: string,
  harness: HarnessRow,
  adapters: readonly BenchmarkAdapterItem[],
): string | null {
  const declared = adapters.find((adapter) => adapter.name === harness.adapter.trim())?.routes
  if (declared === undefined) return null
  const routes = trimPairs(harness.routes)
  for (const row of routes) {
    const route = declared.find((candidate) => candidate.name === row.key)
    if (route === undefined) {
      return `Benchmark ${n} sets a model for the route ${row.key}, which the adapter does not open. Remove the route.`
    }
    if (route.model_source === 'application') {
      return `Benchmark ${n} sets a model for the route ${row.key}, and the adapter picks that model itself. Remove the route.`
    }
  }
  const missing = declared.find(
    (route) =>
      route.model_source === 'caller' &&
      !routes.some((row) => row.key === route.name && CATALOG_MODEL_REGEX.test(row.value)),
  )
  if (missing !== undefined) {
    return `Benchmark ${n} needs a catalog model like provider/model for the route ${missing.name}.`
  }
  return null
}

function validateGroup(n: string, group: GroupRow): string | null {
  if (!SLUG_REGEX.test(group.slug.trim())) {
    return `Benchmark ${n} needs an eval group slug of lowercase letters, digits, hyphens and dots.`
  }
  if (group.display.trim() === '') return `Benchmark ${n} needs a name for its eval group.`
  return null
}

/**
 * The first thing wrong with the list, or null when it can be saved.
 * `adapters` is the shipped list the page read, for each adapter's routes.
 */
export function validateEntries(
  rows: readonly EntryRow[],
  adapters: readonly BenchmarkAdapterItem[],
): string | null {
  for (const [index, row] of rows.entries()) {
    const n = String(index + 1)
    const problem =
      validateSource(n, row.source) ??
      validateHarness(n, row.harness) ??
      validateRoutes(n, row.harness, adapters) ??
      validateGroup(n, row.group)
    if (problem !== null) return problem
  }
  const groups = rows.map((row) => row.group.slug.trim())
  const duplicate = groups.find((slug, index) => groups.indexOf(slug) !== index)
  if (duplicate !== undefined) {
    return `Two benchmarks fill the eval group ${duplicate}. Give each one its own group.`
  }
  return null
}
